import React, { useEffect, useState, useMemo } from 'react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPublishedFundPeriodReportForInvestor } from '../../services/fundPeriodReportService';
import { X, Table2, PieChart } from 'lucide-react';

function formatPaidAmountDisplay(value) {
  if (value == null || value === '') return '—';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return '—';
  const fixed = num.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${withSpaces}.${decPart} $`;
}

function formatDateShort(str, locale) {
  if (!str || typeof str !== 'string') return '—';
  const d = str.slice(0, 10);
  if (!d) return '—';
  try {
    const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(d + 'T12:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

/**
 * @param {{ open: boolean, onClose: () => void, fundInvestorId: string | null, periodKey: string | null }} props
 */
export default function InvestorFundPeriodReportModal({ open, onClose, fundInvestorId, periodKey }) {
  const { t, language } = useLanguage();
  const [tab, setTab] = useState('loans');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!open || !fundInvestorId || !periodKey) {
      setPayload(null);
      setError(null);
      setTab('loans');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPublishedFundPeriodReportForInvestor({ fundInvestorId, periodKey })
      .then((d) => {
        if (!cancelled) setPayload(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, fundInvestorId, periodKey]);

  const rows = payload?.loanPortfolioRows || [];
  const overview = payload?.portfolioOverview;

  const totals = useMemo(() => {
    let interest = 0;
    let quota = 0;
    let paid = 0;
    let div = 0;
    for (const r of rows) {
      interest += Number(r.interestReceived) || 0;
      quota += Number(r.managerQuota) || 0;
      paid += Number(r.paidToInvestors) || 0;
      div += Number(r.dividendsReceived) || 0;
    }
    return {
      interest: Math.round(interest * 100) / 100,
      quota: Math.round(quota * 100) / 100,
      paid: Math.round(paid * 100) / 100,
      div: Math.round(div * 100) / 100,
    };
  }, [rows]);

  useRegisterTopEscapeClose(onClose, open);

  if (!open) return null;

  const rowClass = (kind) => {
    if (kind === 'renewal') return 'bg-amber-50';
    if (kind === 'matured_unrenewed') return 'bg-rose-50';
    return 'bg-white even:bg-gray-50/60';
  };

  return (
    <div
      className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fund-period-report-title"
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white">
          <div>
            <h2 id="fund-period-report-title" className="text-lg font-semibold text-gray-900">
              {t('fundPeriodReport.modalTitle')}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              {periodKey ? `${t('fundPeriodReport.period')} ${periodKey}` : ''}
              {payload?.periodStart && payload?.periodEnd
                ? ` · ${formatDateShort(payload.periodStart, language)} — ${formatDateShort(payload.periodEnd, language)}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label={t('fundPeriodReport.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 px-2 sm:px-4 bg-gray-50/80">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'loans'}
            onClick={() => setTab('loans')}
            className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px ${
              tab === 'loans' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table2 className="w-4 h-4" />
            {t('fundPeriodReport.tabLoans')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'overview'}
            onClick={() => setTab('overview')}
            className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px ${
              tab === 'overview' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <PieChart className="w-4 h-4" />
            {t('fundPeriodReport.tabOverview')}
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5">
          {loading && <p className="text-sm text-gray-600">{t('fundPeriodReport.loading')}</p>}
          {error && !loading && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm px-3 py-2">{error}</div>
          )}
          {!loading && !error && payload && tab === 'loans' && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50 text-amber-900 border border-amber-100">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  {t('fundPeriodReport.legendRenewal')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-rose-50 text-rose-900 border border-rose-100">
                  <span className="w-2 h-2 rounded-full bg-rose-400" />
                  {t('fundPeriodReport.legendMatured')}
                </span>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-x-auto shadow-sm">
                <table className="min-w-[920px] w-full text-sm">
                  <thead>
                    <tr className="bg-slate-800 text-white text-left">
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide">{t('fundPeriodReport.colBorrower')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide">{t('fundPeriodReport.colProperty')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">{t('fundPeriodReport.colLoanDate')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">{t('fundPeriodReport.colMaturity')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide text-right">{t('fundPeriodReport.colPrincipal')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide text-center">{t('fundPeriodReport.colRate')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide text-right">{t('fundPeriodReport.colDividends')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide text-right">{t('fundPeriodReport.colInterest')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide text-right">{t('fundPeriodReport.colQuota')}</th>
                      <th className="px-3 py-2.5 font-semibold text-xs uppercase tracking-wide text-right">{t('fundPeriodReport.colPaidInvestors')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-3 py-8 text-center text-gray-500">
                          {t('fundPeriodReport.emptyLoans')}
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr key={r.dealId || idx} className={rowClass(r.rowKind)}>
                          <td className="px-3 py-2 align-top font-medium text-gray-900">{r.borrowerName || '—'}</td>
                          <td className="px-3 py-2 align-top text-gray-700">{r.propertyAddress || '—'}</td>
                          <td className="px-3 py-2 align-top whitespace-nowrap text-gray-700">{formatDateShort(r.loanStartDate, language)}</td>
                          <td className="px-3 py-2 align-top whitespace-nowrap text-gray-700">{formatDateShort(r.maturityDate, language)}</td>
                          <td className="px-3 py-2 align-top text-right tabular-nums">{formatPaidAmountDisplay(r.principalAmount)}</td>
                          <td className="px-3 py-2 align-top text-center tabular-nums">
                            {r.interestRatePct != null && !Number.isNaN(Number(r.interestRatePct)) ? `${r.interestRatePct} %` : '—'}
                          </td>
                          <td className="px-3 py-2 align-top text-right tabular-nums text-gray-500">{formatPaidAmountDisplay(r.dividendsReceived)}</td>
                          <td className="px-3 py-2 align-top text-right tabular-nums font-medium">{formatPaidAmountDisplay(r.interestReceived)}</td>
                          <td className="px-3 py-2 align-top text-right tabular-nums">{formatPaidAmountDisplay(r.managerQuota)}</td>
                          <td className="px-3 py-2 align-top text-right tabular-nums text-emerald-800 font-medium">{formatPaidAmountDisplay(r.paidToInvestors)}</td>
                        </tr>
                      ))
                    )}
                    {rows.length > 0 && (
                      <tr className="bg-slate-100 font-semibold text-slate-900">
                        <td colSpan={6} className="px-3 py-2.5 text-right">
                          {t('fundPeriodReport.totals')}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{formatPaidAmountDisplay(totals.div)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{formatPaidAmountDisplay(totals.interest)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{formatPaidAmountDisplay(totals.quota)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-900">{formatPaidAmountDisplay(totals.paid)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500">
                {t('fundPeriodReport.quotaNote').replace('{pct}', String(payload.managerInterestQuotaPct ?? 20))}
              </p>
            </div>
          )}

          {!loading && !error && payload && tab === 'overview' && overview && (
            <div className="space-y-8 max-w-3xl">
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('fundPeriodReport.outstandingTitle')}</h3>
                <p className="text-2xl font-bold tabular-nums text-slate-900">{formatPaidAmountDisplay(overview.totalOutstandingLoans)}</p>
                <p className="text-xs text-gray-500 mt-1">{t('fundPeriodReport.outstandingHint')}</p>
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">{t('fundPeriodReport.fundMovementsTitle')}</h3>
                <dl className="grid gap-2 text-sm">
                  <div className="flex justify-between gap-4 border-b border-gray-100 pb-2">
                    <dt className="text-gray-600">{t('fundPeriodReport.capitalPrev')}</dt>
                    <dd className="font-medium tabular-nums">{formatPaidAmountDisplay(overview.fundCapitalPreviousPeriod)}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-gray-100 pb-2">
                    <dt className="text-gray-600">
                      <span>{t('fundPeriodReport.capitalAdded')}</span>
                      <span
                        className="block text-xs text-gray-400 font-normal cursor-help"
                        title={t('fundPeriodReport.capitalAddedTooltip')}
                      >
                        ({t('fundPeriodReport.prorataHint')})
                      </span>
                    </dt>
                    <dd className="font-medium tabular-nums text-emerald-800">{formatPaidAmountDisplay(overview.fundCapitalAddedInPeriod)}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-gray-100 pb-2">
                    <dt className="text-gray-600">{t('fundPeriodReport.capitalRepaid')}</dt>
                    <dd className="font-medium tabular-nums text-rose-800">{formatPaidAmountDisplay(overview.fundCapitalRepaidInPeriod)}</dd>
                  </div>
                  <div className="flex justify-between gap-4 pt-1">
                    <dt className="text-gray-900 font-medium">{t('fundPeriodReport.capitalEnd')}</dt>
                    <dd className="font-bold tabular-nums text-slate-900">{formatPaidAmountDisplay(overview.fundCapitalEndOfPeriod)}</dd>
                  </div>
                </dl>
              </section>

              <section className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 p-4 shadow-sm space-y-3">
                <h3 className="text-sm font-semibold text-indigo-900">{t('fundPeriodReport.upcomingTitle')}</h3>
                <dl className="grid gap-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-700">{t('fundPeriodReport.upcomingAdditions')}</dt>
                    <dd className="font-medium tabular-nums">{formatPaidAmountDisplay(overview.upcomingCapitalAdditions)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-700">{t('fundPeriodReport.upcomingWithdrawals')}</dt>
                    <dd className="font-medium tabular-nums">{formatPaidAmountDisplay(overview.upcomingWithdrawals)}</dd>
                  </div>
                </dl>
                <p className="text-xs text-gray-600">{t('fundPeriodReport.upcomingFootnote')}</p>
              </section>

              {payload.investorReportComment ? (
                <section className="rounded-xl border border-gray-200 bg-amber-50/50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('fundPeriodReport.managerComment')}</h3>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{payload.investorReportComment}</p>
                </section>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
